// CLOSEOUT1 (docs/build-briefs/CLOSEOUT1-closeout-redesign.md): draws the "Where the money went" waterfall as inline SVG from
// the steps in closeoutWaterfallModel.ts. No chart library and no new dependency. Colors are theme tokens only, and state is
// never color alone: a reduction is dashed with a minus sign, an addition carries a plus sign, every column is labeled with its
// words and its figure.

import type { WaterfallModel, WaterfallStep } from "./closeoutWaterfallModel.ts";

const SVG_NS = "http://www.w3.org/2000/svg";
const VIEW_WIDTH = 760;
const VIEW_HEIGHT = 300;
const BASELINE_Y = 246;
const TOP_Y = 56;
const SIDE_PAD = 20;

function svgEl<K extends keyof SVGElementTagNameMap>(
  tag: K,
  attrs: Record<string, string | number>,
): SVGElementTagNameMap[K] {
  const el = document.createElementNS(SVG_NS, tag);
  for (const [name, value] of Object.entries(attrs)) {
    el.setAttribute(name, String(value));
  }
  return el;
}

/** Label words split onto two lines when they would crowd the neighbouring column. */
function labelLines(label: string): string[] {
  if (label.length <= 10 || !label.includes(" ")) return [label];
  const words = label.split(" ");
  const mid = Math.ceil(words.length / 2);
  return [words.slice(0, mid).join(" "), words.slice(mid).join(" ")];
}

function columnPaint(step: WaterfallStep): { fill: string; stroke: string; text: string; dash: string } {
  if (step.kind === "minus") {
    return {
      fill: "var(--color-warning-subtle)",
      stroke: "var(--color-warning-strong)",
      text: "var(--color-warning-strong)",
      dash: "5 3",
    };
  }
  if (step.kind === "plus") {
    return { fill: "var(--color-info-text)", stroke: "none", text: "var(--color-info-text)", dash: "" };
  }
  if (step.key === "gross") {
    return { fill: "var(--color-text-tertiary)", stroke: "none", text: "var(--color-text-primary)", dash: "" };
  }
  return { fill: "var(--color-accent-strong)", stroke: "none", text: "var(--color-text-primary)", dash: "" };
}

export function renderCloseoutWaterfall(host: HTMLElement, model: WaterfallModel): void {
  host.replaceChildren();
  if (model.empty || model.steps.length === 0) {
    const empty = document.createElement("p");
    empty.className = "list-card-empty";
    empty.dataset["testid"] = "closeout-waterfall-empty";
    empty.textContent = model.takeaway;
    host.append(empty);
    return;
  }
  const highest = Math.max(1, ...model.steps.map((step) => Math.max(step.from, step.to)));
  const plotHeight = BASELINE_Y - TOP_Y;
  const yOf = (value: number): number => BASELINE_Y - (Math.max(0, value) / highest) * plotHeight;
  const slot = (VIEW_WIDTH - SIDE_PAD * 2) / model.steps.length;
  const columnWidth = Math.min(96, slot * 0.64);

  const svg = svgEl("svg", {
    viewBox: `0 0 ${VIEW_WIDTH} ${VIEW_HEIGHT}`,
    role: "img",
    "aria-label": model.ariaLabel,
    class: "closeout-waterfall-svg",
  });
  svg.dataset["testid"] = "closeout-waterfall-svg";
  svg.append(
    svgEl("line", {
      x1: SIDE_PAD,
      y1: BASELINE_Y,
      x2: VIEW_WIDTH - SIDE_PAD,
      y2: BASELINE_Y,
      stroke: "var(--color-border-strong)",
    }),
  );

  model.steps.forEach((step, index) => {
    const x = SIDE_PAD + slot * index + (slot - columnWidth) / 2;
    const top = yOf(Math.max(step.from, step.to));
    const bottom = yOf(Math.min(step.from, step.to));
    const height = Math.max(2, bottom - top);
    const paint = columnPaint(step);
    const group = svgEl("g", { "font-size": 13, "text-anchor": "middle" });
    group.dataset["testid"] = "closeout-wf-step";
    group.dataset["key"] = step.key;
    group.dataset["kind"] = step.kind;
    group.dataset["cents"] = String(step.cents);
    const rect = svgEl("rect", {
      x,
      y: top,
      width: columnWidth,
      height,
      rx: 4,
      fill: paint.fill,
    });
    if (paint.stroke !== "none") {
      rect.setAttribute("stroke", paint.stroke);
      rect.setAttribute("stroke-width", "1.5");
      rect.setAttribute("stroke-dasharray", paint.dash);
    }
    const value = svgEl("text", {
      x: x + columnWidth / 2,
      y: top - 10,
      "font-size": step.kind === "total" ? 16 : 14,
      "font-weight": 700,
      fill: paint.text,
    });
    value.textContent = step.valueText;
    group.append(rect, value);
    labelLines(step.label).forEach((line, lineIndex) => {
      const label = svgEl("text", {
        x: x + columnWidth / 2,
        y: BASELINE_Y + 22 + lineIndex * 16,
        fill: step.kind === "total" ? "var(--color-text-primary)" : "var(--color-text-secondary)",
        "font-weight": step.kind === "total" ? 700 : 400,
      });
      label.textContent = line;
      group.append(label);
    });
    svg.append(group);

    const next = model.steps[index + 1];
    if (next !== undefined) {
      const level = yOf(step.to);
      svg.append(
        svgEl("line", {
          x1: x + columnWidth,
          y1: level,
          x2: SIDE_PAD + slot * (index + 1) + (slot - columnWidth) / 2,
          y2: level,
          stroke: "var(--color-border-strong)",
          "stroke-dasharray": "3 3",
        }),
      );
    }
  });
  host.append(svg);
}
